import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Settings2, Palette, UploadCloud, Trash2, Share2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

const FORM_LABELS = {
  requests: "Song requests", dedications: "Dedications", messages: "Messages",
  polls: "Polls", contests: "Contests",
};

export default function EngSettings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const logoRef = useRef(null);
  useEffect(() => { api.get("/engagement/settings").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="p-8 text-sm text-ink-muted">Loading…</div>;

  const save = async (patch) => {
    const next = { ...cfg, ...patch, forms: { ...cfg.forms, ...(patch.forms || {}) } };
    setCfg(next);
    try {
      await api.put("/engagement/settings", {
        forms: next.forms, rate_limit_per_hour: next.rate_limit_per_hour,
        notify_new_request: next.notify_new_request, notify_new_message: next.notify_new_message,
        social_inbox: next.social_inbox });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveBranding = async (patch) => {
    const branding = { ...cfg.branding, ...patch };
    setCfg((c) => ({ ...c, branding }));
    try {
      await api.put("/engagement/branding", {
        accent_color: branding.accent_color ?? "", tagline: branding.tagline ?? "",
        headline: branding.headline ?? "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy("logo");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/engagement/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg((c) => ({ ...c, branding: { ...c.branding, logo_url: r.data.logo_url } }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy("");
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="eng-settings-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Engagement Settings</h1>
        <p className="text-sm text-ink-secondary">Control which listener forms are open and how you're notified.</p>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-2" data-testid="eng-settings-forms">
        <div className="flex items-center gap-2 text-sm font-bold mb-1"><Settings2 className="h-4 w-4 text-brand-amber" /> Public forms</div>
        {Object.entries(FORM_LABELS).map(([k, label]) => (
          <label key={k} className="flex items-center justify-between py-1.5 border-b border-line/40 last:border-0">
            <span className="text-sm">{label}</span>
            <input type="checkbox" checked={cfg.forms?.[k] !== false} data-testid={`eng-set-form-${k}`}
              onChange={(e) => save({ forms: { [k]: e.target.checked } })} />
          </label>
        ))}
        <p className="text-[11px] text-ink-muted">Note: song requests also respect your Request Line settings (auto-approve, hourly limit, banned words).</p>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="eng-settings-limits">
        <div className="text-sm font-bold">Spam protection</div>
        <label className="flex items-center justify-between">
          <span className="text-sm">Max submissions per listener per hour</span>
          <select value={cfg.rate_limit_per_hour} data-testid="eng-set-rate-limit"
            onChange={(e) => save({ rate_limit_per_hour: Number(e.target.value) })}
            className="rounded-md border border-line bg-bg-elevated px-2 py-1 text-sm">
            {[3, 5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-2" data-testid="eng-settings-notify">
        <div className="text-sm font-bold">Notifications</div>
        <label className="flex items-center justify-between py-1">
          <span className="text-sm">Bell alert on new song requests</span>
          <input type="checkbox" checked={cfg.notify_new_request !== false} data-testid="eng-set-notify-request"
            onChange={(e) => save({ notify_new_request: e.target.checked })} />
        </label>
        <label className="flex items-center justify-between py-1">
          <span className="text-sm">Bell alert on new messages & dedications</span>
          <input type="checkbox" checked={cfg.notify_new_message !== false} data-testid="eng-set-notify-message"
            onChange={(e) => save({ notify_new_message: e.target.checked })} />
        </label>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="eng-settings-branding">
        <div className="flex items-center gap-2 text-sm font-bold"><Palette className="h-4 w-4 text-brand-cyan" /> Listener page branding</div>
        <div className="flex items-center gap-3">
          {cfg.branding?.logo_url ? (
            <img src={cfg.branding.logo_url} alt="logo" className="h-12 w-12 rounded-lg object-contain border border-line bg-bg-elevated" data-testid="eng-brand-logo-preview" />
          ) : (
            <div className="h-12 w-12 rounded-lg border border-dashed border-line flex items-center justify-center text-[9px] text-ink-muted">No logo</div>
          )}
          <button onClick={() => logoRef.current?.click()} disabled={busy === "logo"} data-testid="eng-brand-logo-upload"
            className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs font-bold">
            {busy === "logo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />} Upload logo
          </button>
          {cfg.branding?.logo_url && (
            <button onClick={async () => { await api.delete("/engagement/branding/logo"); setCfg((c) => ({ ...c, branding: { ...c.branding, logo_url: "" } })); }}
              data-testid="eng-brand-logo-remove" className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 text-rose-300 px-3 py-1.5 text-xs"><Trash2 className="h-3 w-3" /> Remove</button>
          )}
          <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="eng-brand-logo-input"
            onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Accent color</div>
            <div className="flex items-center gap-2">
              <input type="color" value={cfg.branding?.accent_color || "#fbbf24"} data-testid="eng-brand-accent"
                onChange={(e) => saveBranding({ accent_color: e.target.value })}
                className="h-8 w-12 rounded border border-line bg-bg-elevated cursor-pointer" />
              <span className="text-xs font-mono text-ink-muted">{cfg.branding?.accent_color || "#fbbf24"}</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Headline</div>
            <input value={cfg.branding?.headline || ""} onBlur={(e) => saveBranding({ headline: e.target.value })}
              onChange={(e) => setCfg((c) => ({ ...c, branding: { ...c.branding, headline: e.target.value } }))}
              placeholder="e.g. Join the show!" data-testid="eng-brand-headline" maxLength={120}
              className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-sm" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Tagline</div>
            <input value={cfg.branding?.tagline || ""} onBlur={(e) => saveBranding({ tagline: e.target.value })}
              onChange={(e) => setCfg((c) => ({ ...c, branding: { ...c.branding, tagline: e.target.value } }))}
              placeholder="Shown under your station name" data-testid="eng-brand-tagline" maxLength={160}
              className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-sm" />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-2" data-testid="eng-settings-social-inbox">
        <div className="flex items-center gap-2 text-sm font-bold"><Share2 className="h-4 w-4 text-emerald-300" /> Social Inbox</div>
        <label className="flex items-center justify-between py-1">
          <span className="text-sm">Auto-pull comments & DMs from Facebook, Instagram and Telegram every 15 minutes</span>
          <input type="checkbox" checked={cfg.social_inbox !== false} data-testid="eng-set-social-inbox"
            onChange={(e) => save({ social_inbox: e.target.checked })} />
        </label>
        <p className="text-[11px] text-ink-muted">Pulled items land in your Engagement Inbox. You can also hit "Sync social" on the Inbox page anytime. Platforms whose tokens don't allow reading are skipped safely.</p>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 flex items-center justify-between" data-testid="eng-rewards-link-card">
        <div>
          <div className="text-sm font-bold">Rewards & Loyalty</div>
          <div className="text-[11px] text-ink-muted">Points and badges for requests, votes and contest entries.</div>
        </div>
        <Link to="/dashboard/engagement/rewards" data-testid="eng-rewards-link"
          className="rounded-full bg-brand-amber text-black px-4 py-1.5 text-xs font-bold">Open Rewards</Link>
      </div>
    </div>
  );
}
